import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import type { Tally } from '../stream/useVote';

/// The game screens are drawn for a 1600 x 900 stage that scales to fit the
/// window, so a 1080p stream, a laptop and a phone in landscape all show the
/// same picture.
export const STAGE_W = 1600;
export const STAGE_H = 900;

export function Stage({ children }: { children: ReactNode }) {
  const [scale, setScale] = useState(1);
  useLayoutEffect(() => {
    const update = () => setScale(Math.min(window.innerWidth / STAGE_W, window.innerHeight / STAGE_H));
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, []);
  return (
    <div className="stage-host">
      <div className="stage" style={{ width: STAGE_W, height: STAGE_H, transform: `translate(-50%, -50%) scale(${scale})` }}>
        {children}
      </div>
    </div>
  );
}

export function Button({
  children,
  onClick,
  kind = 'primary',
  disabled,
  className,
  title,
  type = 'button',
}: {
  children: ReactNode;
  onClick?: () => void;
  kind?: 'primary' | 'secondary' | 'ghost' | 'p1' | 'p2';
  disabled?: boolean;
  className?: string;
  title?: string;
  type?: 'button' | 'submit';
}) {
  return (
    <button type={type} className={`btn btn-${kind} ${className ?? ''}`} onClick={onClick} disabled={disabled} title={title}>
      {children}
    </button>
  );
}

export function Modal({ children, onClose, wide }: { children: ReactNode; onClose?: () => void; wide?: boolean }) {
  useEffect(() => {
    const k = (e: KeyboardEvent) => e.key === 'Escape' && onClose?.();
    window.addEventListener('keydown', k);
    return () => window.removeEventListener('keydown', k);
  }, [onClose]);
  return (
    <div className="modal-back" onClick={onClose}>
      <div className={`modal ${wide ? 'wide' : ''}`} role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
        {children}
      </div>
    </div>
  );
}

/// Bars for a chat vote.
export function VoteBars({ tally, labels, colors }: { tally: Tally; labels: string[]; colors: string[] }) {
  const max = Math.max(1, ...tally.counts);
  return (
    <div className="vote-bars">
      {labels.map((l, i) => (
        <div className="vote-row" key={i}>
          <span className="vote-key" style={{ background: colors[i % colors.length] }}>
            {i + 1}
          </span>
          <span className="vote-label">{l}</span>
          <span className="vote-track">
            <span
              className="vote-fill"
              style={{ width: `${(tally.counts[i] / max) * 100}%`, background: colors[i % colors.length] }}
            />
          </span>
          <span className="vote-count">{tally.counts[i]}</span>
        </div>
      ))}
    </div>
  );
}

/// Seconds left of a countdown that starts when [active] turns true.
export function useCountdown(active: boolean, seconds: number, onDone: () => void): number {
  const [left, setLeft] = useState(seconds);
  const done = useRef(onDone);
  done.current = onDone;
  useEffect(() => {
    if (!active) {
      setLeft(seconds);
      return;
    }
    const end = Date.now() + seconds * 1000;
    setLeft(seconds);
    const id = setInterval(() => {
      const l = Math.max(0, Math.ceil((end - Date.now()) / 1000));
      setLeft(l);
      if (l === 0) {
        clearInterval(id);
        done.current();
      }
    }, 250);
    return () => clearInterval(id);
  }, [active, seconds]);
  return left;
}

export function Toggle({
  checked,
  onChange,
  label,
  desc,
  disabled,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  label: string;
  desc?: string;
  disabled?: boolean;
}) {
  return (
    <label className={`toggle ${disabled ? 'disabled' : ''}`}>
      <span className="toggle-text">
        <span className="toggle-label">{label}</span>
        {desc && <span className="toggle-desc">{desc}</span>}
      </span>
      <input type="checkbox" checked={checked} disabled={disabled} onChange={(e) => onChange(e.target.checked)} />
      <span className="toggle-ui" aria-hidden="true" />
    </label>
  );
}

export function Logo({ size = 34 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 512 512" aria-hidden="true">
      <rect width="512" height="512" rx="112" fill="#000" />
      <g transform="translate(256 256) scale(0.95) translate(-368 -367)">
        <circle cx="368" cy="185" r="47" fill="#FFBC58" />
        <circle cx="207" cy="272" r="47" fill="#fff" />
        <circle cx="529" cy="272" r="47" fill="#DC7ECC" />
        <circle cx="368" cy="367" r="47" fill="#fff" />
        <circle cx="207" cy="462" r="47" fill="#58B7FF" />
        <circle cx="529" cy="462" r="47" fill="#fff" />
        <circle cx="368" cy="550" r="47" fill="#fff" />
      </g>
    </svg>
  );
}

export const IOS_URL = 'https://apps.apple.com/app/id6503262430';
export const ANDROID_URL = 'https://play.google.com/store/apps/details?id=com.app.social_gamebox';
export const SITE = 'https://socialgamebox.app';
export const PLAY_HOST = 'play.socialgamebox.app';
